import type {DiffUser} from '@github-ui/diff-lines/types'
import {SingleFileModePullRequestDiffEntry} from './SingleFileModePullRequestDiffEntry'
import {memo, useCallback, useEffect, useMemo, useRef} from 'react'
import {
  useDiffEntries,
  useDiffSummariesArray,
  useDiffSummaryCount,
  useMarkerAndDiffNavigationActions,
  useProgressiveDiffActions,
  useProgressiveDiffEntries,
  useSelectedCommentId,
  useSelectedMarkerId,
  useSelectedPathDigest,
} from '../stores/selectors'
import {EmptyPullRequestBlankSlate} from './EmptyPullRequestBlankSlate'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import {useScrollToAndFocus} from '../hooks/use-scroll-to-and-focus'
import {updateUrlHash} from '@github-ui/history'
import {useSelectedDiffHash} from '@github-ui/diffs/use-selected-diff-hash'

export interface SingleFileDiffEntryNavigatorProps {
  currentUser: DiffUser
  filteredDiffSummaries: PullRequestFileTreeDiff[]
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  pullRequestGlobalRelayId: string
  repository: {
    id: number
    name: string
    ownerLogin: string
  }
}
export const SingleFileModeDiffEntryNavigator = memo(function SingleFileModeDiffEntryNavigator({
  currentUser,
  filteredDiffSummaries,
  headBranchName,
  headRepoName,
  headRepoOwner,
  pullRequestGlobalRelayId,
  repository,
}: SingleFileDiffEntryNavigatorProps) {
  const diffEntriesMap = useDiffEntries()
  const diffEntries = useMemo(() => {
    {
      return Object.values(diffEntriesMap ?? {}).filter(diffEntry => diffEntry !== null)
    }
  }, [diffEntriesMap])
  const diffEntry = diffEntries[0] // Get the first diff entry
  const selectedPathDigest = useSelectedPathDigest()
  const {onScrollToAndFocusEntry, getTargetableEntriesMap, isSelectingLineOrRange} = useScrollToAndFocus()

  const diffSummariesCount = useDiffSummaryCount()
  const firstDiffSummary = useDiffSummariesArray()[0]
  const selectedCommentId = useSelectedCommentId()
  const selectedAnnotationId = useSelectedMarkerId()
  const {navigateToSelectedComment, navigateToSelectedAnnotation, handleHashUpdate} =
    useMarkerAndDiffNavigationActions()
  const {setSelectedHash} = useSelectedDiffHash()
  const isFirstRender = useRef(true)

  const selectedProgressiveDiffEntry = useProgressiveDiffEntries().find(
    entry => entry.pathDigest === selectedPathDigest,
  )

  const {loadMore} = useProgressiveDiffActions()

  const navigate = useCallback(() => {
    if (selectedCommentId) {
      // Navigate to the selected comment
      navigateToSelectedComment(selectedCommentId)
    } else if (selectedAnnotationId && !isNaN(Number(selectedAnnotationId))) {
      // Navigate to the selected annotation
      navigateToSelectedAnnotation(Number(selectedAnnotationId))
    } else if (selectedPathDigest && selectedProgressiveDiffEntry) {
      // Load more content for the selected diff entry
      loadMore(selectedProgressiveDiffEntry)
    } else if (!selectedPathDigest && firstDiffSummary?.pathDigest) {
      // If no path is selected, automatically select the first diff entry and update the URL hash
      setSelectedHash(firstDiffSummary.pathDigest)
      updateUrlHash(`diff-${firstDiffSummary.pathDigest}`)
    }
  }, [
    firstDiffSummary?.pathDigest,
    loadMore,
    navigateToSelectedAnnotation,
    navigateToSelectedComment,
    selectedAnnotationId,
    selectedCommentId,
    selectedPathDigest,
    selectedProgressiveDiffEntry,
    setSelectedHash,
  ])

  /**
   * Run only once, this will navigate to the selected diff, comment or annotation if they exist.
   * and fetch the associated diff entry
   */
  useEffect(() => {
    if (isFirstRender.current) {
      navigate()
      isFirstRender.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Start hash synchronization to sync URL hash with store state
   */
  useEffect(() => {
    const onHashChange = () => {
      handleHashUpdate()
    }

    handleHashUpdate()

    // Listen for hash changes, forward and back button navigation
    window.addEventListener('hashchange', onHashChange)

    return () => {
      window.removeEventListener('hashchange', onHashChange)
    }
  }, [handleHashUpdate])

  /*
   * Empty state for PRs with no changed files.
   */
  if (diffSummariesCount === 0) {
    return <EmptyPullRequestBlankSlate />
  }
  /*
   * Empty state
   * Show a blank slate if nothing matches currently applied filters.
   */
  if (filteredDiffSummaries.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  const pathDigest = selectedPathDigest || diffEntry?.pathDigest || firstDiffSummary?.pathDigest
  if (!pathDigest) return null

  return (
    <div
      ref={(node: HTMLDivElement) => {
        const map = getTargetableEntriesMap()
        if (node) {
          map.set(pathDigest, node)
        } else {
          map.delete(pathDigest)
        }
      }}
    >
      <SingleFileModePullRequestDiffEntry
        diffEntryPathDigest={pathDigest}
        currentUser={currentUser}
        diffManuallyExpanded={false}
        filteredDiffSummaries={filteredDiffSummaries}
        headBranchName={headBranchName}
        headRepoName={headRepoName}
        headRepoOwner={headRepoOwner}
        pullRequestGlobalRelayId={pullRequestGlobalRelayId}
        repository={repository}
        onScrollToAndFocusEntry={onScrollToAndFocusEntry}
        isSelectingLineOrRange={isSelectingLineOrRange}
      />
    </div>
  )
})

try{ SingleFileModeDiffEntryNavigator.displayName ||= 'SingleFileModeDiffEntryNavigator' } catch {}